import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Camera,
  Upload,
  X,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Maximize2,
  Trash2,
  GripVertical
} from 'lucide-react'
import { uploadFile, getFileUrl, deleteFile } from '../lib/supabase'

// Image compression utility using canvas API
const compressImage = async (file, maxWidth = 1920, maxSize = 2 * 1024 * 1024) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (blob.size > maxSize) {
              // If still too large, reduce quality further
              canvas.toBlob(
                (compressedBlob) => {
                  resolve(compressedBlob)
                },
                'image/jpeg',
                0.7
              )
            } else {
              resolve(blob)
            }
          },
          'image/jpeg',
          0.85
        )
      }

      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target.result
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

const PhotoGallery = ({
  photos = [],
  vehicleId,
  userId,
  editable = false,
  onPhotosChange,
  maxPhotos = 20,
  compact = false
}) => {
  const [primaryPhotoId, setPrimaryPhotoId] = useState(photos.find(p => p.is_primary)?.id || null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadProgress, setUploadProgress] = useState({})
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)
  const dragRef = useRef(null)

  // Handle primary image selection
  const handleSetPrimary = async (photoId) => {
    if (primaryPhotoId === photoId) return

    setPrimaryPhotoId(photoId)

    // Call parent callback to update database
    if (onPhotosChange) {
      onPhotosChange(photos.map(p => ({
        ...p,
        is_primary: p.id === photoId
      })))
    }
  }

  // Open lightbox
  const openLightbox = (index) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  // Navigate lightbox
  const nextPhoto = () => {
    setLightboxIndex((prev) => (prev + 1) % photos.length)
  }

  const prevPhoto = () => {
    setLightboxIndex((prev) => (prev - 1 + photos.length) % photos.length)
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextPhoto()
      if (e.key === 'ArrowLeft') prevPhoto()
      if (e.key === 'Escape') setLightboxOpen(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, photos.length])

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = [...(e.dataTransfer?.files || [])]
    handleFileSelection(files)
  }

  // Handle file selection
  const handleFileSelection = (files) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'))

    if (imageFiles.length === 0) {
      setError('Please select image files only')
      return
    }

    const canAdd = maxPhotos - photos.length
    if (imageFiles.length > canAdd) {
      setError(`You can only add ${canAdd} more photo(s)`)
      return
    }

    setError(null)
    setSelectedFiles(prev => [...prev, ...imageFiles])
  }

  // Remove selected file
  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Upload files
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return

    setUploading(true)
    const newPhotos = [...photos]

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i]
        const fileKey = `file-${i}`

        try {
          setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }))

          // Compress image
          const compressedBlob = await compressImage(file)

          // Generate unique path
          const timestamp = Date.now()
          const fileName = `${timestamp}-${i}.jpg`
          const path = `vehicle-images/${userId}/${vehicleId}/${fileName}`

          // Upload to Supabase
          const { data, error: uploadError } = await uploadFile('vehicle-images', path, compressedBlob)

          if (uploadError) throw uploadError

          // Get public URL
          const photoUrl = getFileUrl('vehicle-images', path)

          // Add to photos array
          newPhotos.push({
            id: `${timestamp}-${i}`,
            url: photoUrl,
            is_primary: newPhotos.length === 0, // First photo is primary
            storage_path: path
          })

          setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }))
        } catch (err) {
          console.error(`Failed to upload file ${i}:`, err)
          setUploadProgress(prev => ({ ...prev, [fileKey]: -1 }))
        }
      }

      // Update primary if needed
      if (primaryPhotoId === null && newPhotos.length > 0) {
        setPrimaryPhotoId(newPhotos[0].id)
        newPhotos[0].is_primary = true
      }

      // Call parent callback
      if (onPhotosChange) {
        onPhotosChange(newPhotos)
      }

      // Clear selected files
      setSelectedFiles([])
      setUploadProgress({})
    } catch (err) {
      console.error('Upload error:', err)
      setError('Failed to upload photos. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  // Delete photo
  const deletePhoto = async (photoId) => {
    if (!window.confirm('Remove this photo?')) return

    try {
      const photo = photos.find(p => p.id === photoId)
      if (photo?.storage_path) {
        await deleteFile('vehicle-images', photo.storage_path)
      }

      const updatedPhotos = photos.filter(p => p.id !== photoId)

      // If deleted photo was primary, set new primary
      if (photoId === primaryPhotoId && updatedPhotos.length > 0) {
        updatedPhotos[0].is_primary = true
        setPrimaryPhotoId(updatedPhotos[0].id)
      } else if (updatedPhotos.length === 0) {
        setPrimaryPhotoId(null)
      }

      if (onPhotosChange) {
        onPhotosChange(updatedPhotos)
      }
    } catch (err) {
      console.error('Delete error:', err)
      setError('Failed to delete photo. Please try again.')
    }
  }

  // View mode (no uploads)
  if (!editable) {
    const primaryPhoto = photos.find(p => p.is_primary) || photos[0]
    const otherPhotos = photos.filter(p => p.id !== primaryPhoto?.id)

    if (photos.length === 0) {
      return (
        <div className={`${compact ? 'h-48' : 'h-96'} bg-rdc-cream rounded-xl border border-rdc-stone flex items-center justify-center`}>
          <div className="text-center">
            <ImageIcon className="w-12 h-12 text-rdc-taupe mx-auto mb-2 opacity-50" />
            <p className="text-rdc-taupe text-sm">No photos available</p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Hero image */}
        <div className="relative overflow-hidden rounded-xl border border-rdc-stone bg-rdc-cream aspect-video">
          <img
            src={primaryPhoto.url}
            alt="Vehicle"
            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
            onClick={() => openLightbox(0)}
          />
          <button
            onClick={() => openLightbox(0)}
            className="absolute top-4 right-4 bg-black/30 hover:bg-black/50 text-white p-2 rounded-lg transition-colors"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1 rounded-full font-medium">
            {photos.findIndex(p => p.id === primaryPhoto.id) + 1} / {photos.length}
          </div>
        </div>

        {/* Thumbnail grid */}
        {otherPhotos.length > 0 && (
          <div className={compact ? 'grid grid-cols-4 gap-2' : 'grid grid-cols-6 gap-2'}>
            {otherPhotos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => {
                  handleSetPrimary(photo.id)
                  setLightboxIndex(photos.findIndex(p => p.id === photo.id))
                }}
                className="relative group overflow-hidden rounded-lg aspect-square border-2 border-transparent hover:border-rdc-orange transition-colors"
              >
                <img
                  src={photo.url}
                  alt={`Vehicle thumbnail ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </button>
            ))}
          </div>
        )}

        {/* Lightbox */}
        {lightboxOpen && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Main image */}
            <div className="relative w-full h-full max-w-4xl max-h-4xl flex items-center justify-center p-4">
              <img
                src={photos[lightboxIndex].url}
                alt="Full size"
                className="max-w-full max-h-full object-contain"
              />

              {/* Navigation */}
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium">
                {lightboxIndex + 1} / {photos.length}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Edit mode (with uploads)
  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <X className="w-4 h-4" />
          </div>
          <p>{error}</p>
        </div>
      )}

      {/* Photo count */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-rdc-charcoal">
          Photos ({photos.length + selectedFiles.length} / {maxPhotos})
        </h3>
        {photos.length + selectedFiles.length >= maxPhotos && (
          <p className="text-xs text-rdc-taupe">Max photos reached</p>
        )}
      </div>

      {/* Current photos display */}
      {photos.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-rdc-taupe">Current Photos</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className={`relative group overflow-hidden rounded-lg aspect-square border-2 transition-all ${
                  photo.id === primaryPhotoId
                    ? 'border-rdc-orange shadow-md'
                    : 'border-rdc-stone hover:border-rdc-orange'
                }`}
              >
                <img
                  src={photo.url}
                  alt="Vehicle"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {photo.id !== primaryPhotoId && (
                    <button
                      onClick={() => handleSetPrimary(photo.id)}
                      className="bg-rdc-orange hover:bg-rdc-primary-dark text-white p-2 rounded-lg transition-colors"
                      title="Set as primary"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deletePhoto(photo.id)}
                    className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Primary badge */}
                {photo.id === primaryPhotoId && (
                  <div className="absolute top-2 left-2 bg-rdc-orange text-white text-xs px-2 py-1 rounded font-medium">
                    Primary
                  </div>
                )}

                {/* Upload progress */}
                {uploadProgress[photo.id] !== undefined && uploadProgress[photo.id] !== 100 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-white text-xs font-medium">
                      {uploadProgress[photo.id] === -1 ? 'Error' : `${uploadProgress[photo.id]}%`}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected files preview */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-rdc-taupe">Selected Photos ({selectedFiles.length})</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {selectedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="relative group overflow-hidden rounded-lg aspect-square border-2 border-dashed border-rdc-orange bg-rdc-cream"
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Selected ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => removeSelectedFile(index)}
                    className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {Math.round(file.size / 1024)}KB
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drag and drop zone */}
      {photos.length + selectedFiles.length < maxPhotos && (
        <div
          ref={dragRef}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 transition-colors text-center cursor-pointer ${
            dragActive
              ? 'border-rdc-orange bg-rdc-orange/5'
              : 'border-rdc-stone bg-rdc-cream/50 hover:border-rdc-orange'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileSelection(Array.from(e.target.files || []))}
            className="hidden"
            disabled={uploading}
          />

          <div className="space-y-3">
            <div className="flex justify-center">
              <div className="bg-rdc-orange/10 p-3 rounded-lg">
                <Upload className="w-6 h-6 text-rdc-orange" />
              </div>
            </div>

            <div>
              <p className="text-rdc-charcoal font-medium">Drag photos here or click to browse</p>
              <p className="text-xs text-rdc-taupe mt-1">
                Supports JPG, PNG, WebP (max 20 photos, auto-compressed to 2MB)
              </p>
            </div>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-rdc-orange hover:bg-rdc-primary-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <span className="flex items-center gap-2 justify-center">
                <Camera className="w-4 h-4" />
                Select Photos
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Upload button */}
      {selectedFiles.length > 0 && (
        <div className="flex gap-3">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="flex-1 bg-rdc-orange hover:bg-rdc-primary-dark text-white px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
          </button>
          <button
            onClick={() => {
              setSelectedFiles([])
              setUploadProgress({})
            }}
            disabled={uploading}
            className="px-4 py-2.5 rounded-lg border border-rdc-stone text-rdc-charcoal hover:bg-rdc-cream transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Tips */}
      <div className="bg-rdc-cream/50 border border-rdc-stone rounded-lg p-4 text-xs text-rdc-taupe space-y-2">
        <p className="font-medium text-rdc-charcoal">Tips for great photos:</p>
        <ul className="space-y-1 ml-3">
          <li>Use good lighting to show off the vehicle's condition</li>
          <li>Include multiple angles (front, side, rear, interior)</li>
          <li>First photo will be used as the primary image</li>
          <li>Images are automatically compressed to save space</li>
        </ul>
      </div>
    </div>
  )
}

export default PhotoGallery
